import { apiClient } from "@/lib/apiClient";
import {
  CreateTicketDto,
  UpdateTicketDto,
  CreateTicketMessageDto,
  FetchTicketsOptions,
  SearchTicketsOptions,
} from "@/types/models/ticket";

// Ticket CRUD Operations
export const getTickets = async (options: FetchTicketsOptions = {}) => {
  const params: Record<string, string> = {};

  if (options.page) params.page = options.page.toString();
  if (options.limit) params.limit = options.limit.toString();
  if (options.sortBy) params.sortBy = options.sortBy;
  if (options.sortOrder) params.sortOrder = options.sortOrder;

  // Add filters
  if (options.filters) {
    if (options.filters.status) params.status = options.filters.status;
    if (options.filters.priority) params.priority = options.filters.priority;
    if (options.filters.type) params.type = options.filters.type;
    if (options.filters.assignedTo)
      params.assignedTo = options.filters.assignedTo;
    if (options.filters.reporterId)
      params.reporterId = options.filters.reporterId;
    if (options.filters.createdAfter)
      params.createdAfter = options.filters.createdAfter;
    if (options.filters.createdBefore)
      params.createdBefore = options.filters.createdBefore;
    if (options.filters.dueAfter) params.dueAfter = options.filters.dueAfter;
    if (options.filters.dueBefore) params.dueBefore = options.filters.dueBefore;
    if (options.filters.tags && options.filters.tags.length > 0) {
      params.tags = options.filters.tags.join(",");
    }
  }

  return await apiClient.getPaginated("/api/v1/admin/:storeId/tickets", params);
};

export const getTicketById = async (ticketId: string) => {
  return await apiClient.get(`/api/v1/admin/:storeId/tickets/${ticketId}`);
};

export const searchTickets = async (options: SearchTicketsOptions) => {
  const params: Record<string, string> = {
    q: options.q,
  };

  if (options.page) params.page = options.page.toString();
  if (options.limit) params.limit = options.limit.toString();

  // Add filters
  if (options.filters) {
    if (options.filters.status) params.status = options.filters.status;
    if (options.filters.priority) params.priority = options.filters.priority;
    if (options.filters.type) params.type = options.filters.type;
    if (options.filters.assignedTo)
      params.assignedTo = options.filters.assignedTo;
    if (options.filters.reporterId)
      params.reporterId = options.filters.reporterId;
    if (options.filters.tags && options.filters.tags.length > 0) {
      params.tags = options.filters.tags.join(",");
    }
  }

  return await apiClient.getPaginated(
    "/api/v1/admin/:storeId/tickets/search",
    params,
  );
};

export const createTicket = async (ticket: CreateTicketDto) => {
  return await apiClient.post("/api/v1/admin/:storeId/tickets", ticket);
};

export const updateTicket = async (
  ticketId: string,
  ticket: UpdateTicketDto,
) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/tickets/${ticketId}`,
    ticket,
  );
};

export const deleteTicket = async (ticketId: string) => {
  return await apiClient.delete(`/api/v1/admin/:storeId/tickets/${ticketId}`);
};

// Ticket Messages
export const getTicketComments = async (
  ticketId: string,
  page = 1,
  limit = 50,
) => {
  const params = {
    page: page.toString(),
    limit: limit.toString(),
  };

  return await apiClient.getPaginated(
    `/api/v1/admin/:storeId/tickets/${ticketId}/comments`,
    params,
  );
};

export const createTicketComment = async (comment: CreateTicketMessageDto) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/tickets/${comment.ticketId}/comments`,
    {
      message: comment.message,
      isPrivate: comment.isPrivate,
    },
  );
};

export const markTicketMessageAsRead = async (
  ticketId: string,
  messageId: string,
) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/tickets/${ticketId}/messages/${messageId}/read`,
  );
};

// Ticket Assignment
export const assignTicket = async (ticketId: string, assignedTo: string) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/tickets/${ticketId}/assign`,
    { assignedTo },
  );
};

export const unassignTicket = async (ticketId: string) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/tickets/${ticketId}/unassign`,
  );
};

// Ticket Status Updates
export const updateTicketStatus = async (ticketId: string, status: string) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/tickets/${ticketId}/status`,
    { status },
  );
};

export const updateTicketPriority = async (
  ticketId: string,
  priority: string,
) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/tickets/${ticketId}/priority`,
    { priority },
  );
};

// Ticket Tags
export const getTicketTags = async () => {
  return await apiClient.get("/api/v1/admin/:storeId/tickets/tags");
};

export const createTicketTag = async (tag: { name: string; color: string }) => {
  return await apiClient.post("/api/v1/admin/:storeId/tickets/tags", tag);
};

export const addTicketTag = async (ticketId: string, tagId: string) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/tickets/${ticketId}/tags/${tagId}`,
  );
};

export const removeTicketTag = async (ticketId: string, tagId: string) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/tickets/${ticketId}/tags/${tagId}`,
  );
};

// Participants
export const addTicketParticipant = async (
  ticketId: string,
  participantEmail: string,
) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/tickets/${ticketId}/participants`,
    { email: participantEmail },
  );
};

export const removeTicketParticipant = async (
  ticketId: string,
  participantId: string,
) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/tickets/${ticketId}/participants/${participantId}`,
  );
};

// Analytics
export const getTicketStats = async (
  period: "day" | "week" | "month" | "year" = "month",
) => {
  return await apiClient.get(
    `/api/v1/admin/:storeId/tickets/stats?period=${period}`,
  );
};

export const getAgentStats = async (agentId?: string) => {
  const params = agentId ? { agentId } : {};
  return await apiClient.get(
    "/api/v1/admin/:storeId/tickets/agent-stats",
    params,
  );
};

// Bulk Operations
export const bulkUpdateTickets = async (
  ticketIds: string[],
  updates: Partial<UpdateTicketDto>,
) => {
  return await apiClient.put("/api/v1/admin/:storeId/tickets/bulk", {
    ticketIds,
    updates,
  });
};

export const bulkDeleteTickets = async (ticketIds: string[]) => {
  return await apiClient.delete("/api/v1/admin/:storeId/tickets/bulk", {
    ticketIds,
  });
};

// File Attachments
export const uploadTicketAttachment = async (ticketId: string, file: File) => {
  return await apiClient.uploadFile(
    `/api/v1/admin/:storeId/tickets/${ticketId}/attachments`,
    file,
  );
};

export const deleteTicketAttachment = async (
  ticketId: string,
  attachmentId: string,
) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/tickets/${ticketId}/attachments/${attachmentId}`,
  );
};
